import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import './BuildSetsPanel.css';
import SetCard from '../search/SetCard';
import { processItemData } from '../../core/statProcessing';
import SearchService from '../../services/SearchService';
import { useResponsive } from '../../hooks/useResponsive';
import { useEquipItem } from '../../hooks/useEquipItem';
import { setNameField } from '../../core/setBonusesFromCatalog';

const TXT_ITEM_SET = { fr: 'panoplie', en: 'item set', de: 'Set', es: 'set', pt: 'conjunto' };
const TXT_ITEM_SETS = { fr: 'panoplies', en: 'item sets', de: 'Sets', es: 'sets', pt: 'conjuntos' };
const TXT_NO_ITEM_SETS = { fr: 'Aucune panoplie', en: 'No item sets', de: 'Keine Sets', es: 'Ningún set', pt: 'Nenhum conjunto' };

const BuildSetsPanel = ({ build, setBuild, lang = 'fr', onTooltipShow, onTooltipHide, onSetTooltipShow, onSetTooltipHide, isGenerating = false, isServerUpdate = false, showTitle = false, canEditBuild = true, variant = null, hideWhenEmpty = false }) => {
    const fetchingSetsRef = useRef(new Set());
    const { isMobile } = useResponsive();
    // `variant` forces a compact style regardless of viewport (e.g. the build
    // tooltip wants the mobile 'strip' look on desktop); default keeps the
    // responsive strip-on-mobile / panel-on-desktop behavior.
    const compactVariant = variant || (isMobile ? 'strip' : 'panel');
    const { equipSet, equipItem } = useEquipItem(lang);
    const handleSetClick = useCallback((set) => {
        equipSet(set);
    }, [equipSet]);
    const handleItemClick = useCallback((item) => {
        equipItem(item);
    }, [equipItem]);

    // Fetch complete set data for sets that don't have full information
    useEffect(() => {
        if (!canEditBuild || !build || !build.setBonuses || !setBuild) return;

        Object.entries(build.setBonuses).forEach(([setId, setData]) => {
            if (setData && typeof setData === 'object') {
                const pieces = setData.pieces || 0;
                const allBonuses = setData.bonuses || [];
                const hasBonuses = Array.isArray(allBonuses) && allBonuses.length > 0;

                // Fetch complete set data if:
                // 1. Set has 2+ pieces
                // 2. Missing bonuses (indicates manually added set that needs full data)
                // 3. Not already fetching
                const needsFetch = pieces >= 2 &&
                    !hasBonuses &&
                    !fetchingSetsRef.current.has(setId);

                if (needsFetch) {
                    fetchingSetsRef.current.add(setId);

                    SearchService.getSet(setId, lang)
                        .then(setInfo => {
                            if (!setInfo) return;
                            const allSetItems = setInfo.items || [];
                            // Apply against the *latest* build, not the closure captured
                            // when the fetch started — otherwise items equipped while the
                            // request was in flight get reverted, desyncing the stat total.
                            setBuild(prevBuild => {
                                const live = prevBuild?.setBonuses?.[setId];
                                // Set gone, or already filled by another path: leave as-is.
                                if (!live || (Array.isArray(live.bonuses) && live.bonuses.length > 0)) {
                                    return prevBuild;
                                }
                                const liveItemIds = new Set(
                                    prevBuild.items.filter(item => item && item.id).map(item => String(item.id))
                                );
                                const activeItems = [];
                                const inactiveItems = [];
                                allSetItems.forEach(item => {
                                    const itemId = item && item.id ? String(item.id) : null;
                                    if (itemId && liveItemIds.has(itemId)) {
                                        activeItems.push(item);
                                    } else {
                                        inactiveItems.push(item);
                                    }
                                });
                                const updatedSetBonuses = { ...prevBuild.setBonuses };
                                updatedSetBonuses[setId] = {
                                    name: setNameField(setInfo.name || live.name, setId),
                                    pieces: activeItems.length, // recomputed from the live build
                                    bonuses: setInfo.bonuses || [],
                                    // Only Cire Momore carries any; see core/set_caps.py.
                                    bonus_caps: setInfo.bonus_caps || undefined,
                                    active_items: activeItems,
                                    inactive_items: inactiveItems,
                                    max_pieces: setInfo.max_pieces || allSetItems.length
                                };
                                return prevBuild.setSetBonuses(updatedSetBonuses);
                            });
                        })
                        .catch(error => {
                            console.error(`Failed to fetch set ${setId}:`, error);
                        })
                        .finally(() => {
                            // Allow a later refetch if the set still needs bonuses
                            // (e.g. the update opted out because the build moved on).
                            fetchingSetsRef.current.delete(setId);
                        });
                }
            }
        });
    }, [build, lang, setBuild, canEditBuild]);

    const activeSets = useMemo(() => {
        if (!build || !build.setBonuses) return [];

        const sets = [];
        Object.entries(build.setBonuses).forEach(([setId, setData]) => {
            // Handle new format: { name, pieces, bonuses, active_items, inactive_items, max_pieces }
            if (setData && typeof setData === 'object' && setData.bonuses !== undefined) {
                const pieces = setData.pieces || 0;
                const allBonuses = setData.bonuses || [];
                const activeItems = setData.active_items || [];
                const inactiveItems = setData.inactive_items || [];
                const maxPieces = setData.max_pieces || 0;

                // Include sets with at least 2 pieces (even if bonuses are empty/being fetched)
                if (pieces >= 2 && Array.isArray(allBonuses)) {
                    // Create a map of build items by ID for quick lookup
                    const buildItemsMap = new Map();
                    build.items.forEach(buildItem => {
                        if (buildItem && buildItem.id) {
                            buildItemsMap.set(String(buildItem.id), buildItem);
                        }
                    });

                    // Process active items: merge with build items to get complete data
                    const processedActiveItems = activeItems
                        .map(item => {
                            if (!item) return null;
                            const itemId = item.id ? String(item.id) : null;
                            // Merge server item data with build item data (server data takes precedence, but add any missing fields from build)
                            const buildItem = itemId ? buildItemsMap.get(itemId) : null;
                            const mergedItem = buildItem ? { ...buildItem, ...item } : item;
                            return processItemData(mergedItem);
                        })
                        .filter(item => item !== null)
                        .map(item => ({ ...item, isActive: true }));

                    // Process inactive items: use server data directly (they're not in build)
                    const processedInactiveItems = processItemData(inactiveItems)
                        .filter(item => item !== null)
                        .map(item => ({ ...item, isActive: false }));

                    // Combine active and inactive items (active first)
                    const processedItems = [...processedActiveItems, ...processedInactiveItems];

                    // Both serializers carry the localized dict now. Wrapping a
                    // bare string as `{ [lang]: name }` was what filed French
                    // text under the reader's own language key.
                    const setName = setNameField(setData.name, setId);

                    // Transform to SetCard format
                    const setObj = {
                        id: setId,
                        name: setName,
                        items: processedItems, // All set items (active + inactive) with full data
                        bonuses: allBonuses, // All bonus tiers in format: [{ num_items_required: 2, effects: [...] }, ...]
                        bonus_caps: setData.bonus_caps, // ceilings, when they came in a parallel list
                        max_pieces: maxPieces || processedItems.length,
                        pieces: pieces
                    };

                    sets.push(setObj);
                }
            }
        });

        // Language-independent now that the set name is carried as a dict.
        return sets;
    }, [build]);

    const setCount = activeSets.length;
    const setCountText = setCount === 1
        ? (TXT_ITEM_SET[lang] || TXT_ITEM_SET.en)
        : (TXT_ITEM_SETS[lang] || TXT_ITEM_SETS.en);

    if (setCount === 0) {
        if (hideWhenEmpty) return null;
        return (
            <div className="build-sets-panel">
                <div className="no-sets">
                    <h6>{TXT_NO_ITEM_SETS[lang] || TXT_NO_ITEM_SETS.en}</h6>
                </div>
            </div>
        );
    }

    return (
        <div className="build-sets-panel column">
            {showTitle && setCount !== 0 && <h4 className="build-sets-panel-title">{setCount} {setCountText}</h4>}
            <div className="build-sets-panel-list">
                {activeSets.map((set) => (
                    <SetCard
                        key={set.id}
                        set={set}
                        lang={lang}
                        className="set-card--in-build-panel"
                        onSetClick={handleSetClick}
                        onItemClick={handleItemClick}
                        onTooltipShow={onTooltipShow}
                        onTooltipHide={onTooltipHide}
                        compact={true}
                        compactVariant={compactVariant}
                        isGenerating={isGenerating}
                        isServerUpdate={isServerUpdate}
                    />
                ))}
            </div>
        </div>
    );
};

export default BuildSetsPanel;
